/**
 * @fileoverview Provides React components for creating styled tables, including `TableContainer`, `Table`, `THead`, `TBody`, `Tr`, `Th`, and `Td`. Includes `RowExpander` for expandable rows and the `useRowExpansion` hook to manage expansion state.
 */

import React, { useState } from 'react'
import '../styles/components/Table.css'

// Custom hook for managing row expansion state
export const useRowExpansion = () => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }))
  }

  const isExpanded = (rowId: string) => expandedRows[rowId] || false

  const getToggleProps = (rowId: string) => ({
    isExpanded: isExpanded(rowId),
    onToggle: () => toggleRowExpansion(rowId),
  })

  return {
    expandedRows,
    toggleRowExpansion,
    isExpanded,
    getToggleProps,
  }
}

interface TableContainerProps {
  children: React.ReactNode
  className?: string
}

export const TableContainer: React.FC<TableContainerProps> = ({ children, className = '' }) => {
  return <div className={`table-container ${className}`}>{children}</div>
}

interface TableProps {
  children: React.ReactNode
  className?: string
}

export const Table: React.FC<TableProps> = ({ children, className = '' }) => {
  return <table className={`table ${className}`}>{children}</table>
}

interface THeadProps {
  children: React.ReactNode
  className?: string
}

export const THead: React.FC<THeadProps> = ({ children, className = '' }) => {
  return <thead className={`table-header ${className}`}>{children}</thead>
}

interface TBodyProps {
  children: React.ReactNode
  className?: string
}

export const TBody: React.FC<TBodyProps> = ({ children, className = '' }) => {
  return <tbody className={`table-body ${className}`}>{children}</tbody>
}

interface TrProps {
  children: React.ReactNode
  className?: string
}

export const Tr: React.FC<TrProps> = ({ children, className = '' }) => {
  return <tr className={`table-row ${className}`}>{children}</tr>
}

interface ThProps {
  children: React.ReactNode
  className?: string
}

export const Th: React.FC<ThProps> = ({ children, className = '' }) => {
  return <th className={`header-cell ${className}`}>{children}</th>
}

interface TdProps {
  children: React.ReactNode
  className?: string
}

export const Td: React.FC<TdProps> = ({ children, className = '' }) => {
  return <td className={`table-cell ${className}`}>{children}</td>
}

interface RowExpanderProps {
  children: React.ReactNode
  expandedContent: React.ReactNode
  className?: string
  hasExpandableContent?: boolean
  isExpanded?: boolean
  onToggle?: () => void
  colSpan?: number
}

export const RowExpander: React.FC<RowExpanderProps> = ({
  children,
  expandedContent,
  className = '',
  hasExpandableContent = true,
  isExpanded: controlledIsExpanded,
  onToggle,
  colSpan = 5,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false)

  const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalExpanded

  const handleToggle = () => {
    if (onToggle) {
      onToggle()
    } else {
      setInternalExpanded(!internalExpanded)
    }
  }

  return (
    <>
      <tr
        className={`table-row ${hasExpandableContent ? 'clickable-row' : ''} ${isExpanded ? 'expanded-parent' : ''} ${className}`}
        onClick={hasExpandableContent ? handleToggle : undefined}
      >
        {children}
      </tr>
      {hasExpandableContent && isExpanded && (
        <tr className="expanded-row">
          <td colSpan={colSpan}>{expandedContent}</td>
        </tr>
      )}
    </>
  )
}
